import { ComponentType, ReactNode, createContext } from "react";
import type {
  IPortfolioMetadata,
  IAccountMetadata,
} from "@pulse-ui/codegen/generated_ts/org_metadata";

/**
 * Group IDs for linked widgets
 * Widgets with the same groupId share state (e.g., symbol selection)
 */
export const WIDGET_GROUPS = [
  "default",
  "group-1",
  "group-2",
  "group-3",
  "group-4",
  "group-5",
] as const;

export type GroupId = (typeof WIDGET_GROUPS)[number];

/**
 * Props passed to every widget instance
 */
export interface WidgetProps {
  /**
   * Unique instance ID for this widget
   * Used for per-instance state (controls, settings)
   */
  instanceId: string;

  /**
   * Optional group ID for linked widgets
   * Widgets with same groupId share data (e.g., selected symbol)
   */
  groupId?: GroupId;

  /**
   * Optional static configuration from registry
   * Set at widget definition time, same for all instances
   */
  config?: Record<string, unknown>;
}

/**
 * Widget component type
 */
export type Widget = ComponentType<WidgetProps>;

/**
 * Widget definition in registry
 */
export interface WidgetDefinition {
  /** Unique widget ID (kebab-case) */
  id: string;

  /** Display name */
  name: string;

  /** Short description */
  description: string;

  /** Category for grouping in library */
  category: string;

  /** Lazy-loaded component */
  component: Widget;

  /** Optional icon name (Tabler Icons) */
  icon?: string;

  /** Optional static config */
  config?: Record<string, unknown>;

  /** When true, only visible to admin users */
  adminOnly?: boolean;

  /** When true, widget appears in the library but cannot be dragged onto the workspace */
  disabled?: boolean;

  /**
   * When true, widget is omitted from the drag library entirely.
   * Use for widgets that are only opened programmatically (e.g. detail
   * widgets spawned by useLayoutActions().openWidget). The widget is still
   * resolvable by id when present in a saved layout.
   */
  hideFromLibrary?: boolean;

  /**
   * When set, widget is only visible to users who have at least one of the listed roles.
   * Roles are string values from /api/role/v1/{orgUserId} (e.g. "trader", "admin").
   * Takes precedence over adminOnly when both are present.
   */
  requiredRoles?: string[];

  /** When false, widget does not participate in group linking (default: true) */
  isLinkable?: boolean;
}

/**
 * Shared state for widgets in the same group.
 * Serializable — safe to store in Redux.
 */
export interface GroupSharedState {
  /** Currently selected trading symbol (e.g., "BTC-USD") */
  selectedSymbol?: string;
  /** Currently selected timeframe (e.g., "1h", "1d") */
  selectedTimeframe?: string;
  /** Currently selected account (legacy — prefer selectedAccountId) */
  selectedAccount?: string;
  /** Currently selected portfolio UUID */
  selectedPortfolioId?: string;
  /** Currently selected account UUID (or "all-accounts") */
  selectedAccountId?: string;
  /** Currently selected quote asset for notional views (e.g., "USDT", "BTC") */
  selectedQuoteAsset?: string;
  /** Limit price pushed by a market-depth widget for order pre-fill */
  selectedPrice?: number | string;
  /** Quantity pushed by a market-depth widget for order pre-fill */
  selectedQuantity?: number | string;
  /** Order side ("bid" | "ask") pushed by a market-depth widget for order pre-fill */
  selectedSide?: "bid" | "ask";
}

/**
 * Context value provided by host apps (cloud-ui uses Redux, Halo uses Jotai fallback).
 * Widgets never import this directly — they call useGroupSharedState() instead.
 */
export interface GroupStateContextValue {
  getGroupState: (groupId: GroupId) => GroupSharedState;
  setGroupState: (
    groupId: GroupId,
    updater: (prev: GroupSharedState) => GroupSharedState,
  ) => void;
}

/**
 * React context for group shared state.
 * Provide this via ReduxGroupStateProvider (cloud-ui) or leave unprovided (Halo — Jotai fallback).
 */
export const GroupStateContext = createContext<GroupStateContextValue | null>(
  null,
);

/**
 * Shape of the portfolio/account data exposed by the dashboard provider.
 * Populated by DashboardDataProvider in cloud-ui; widgets consume via hooks in this package.
 */
export interface PortfolioDataContextValue {
  portfolios: IPortfolioMetadata[];
  pricingAssets: string[];
  isLoading: boolean;
  error: boolean;
}

/**
 * Context for dashboard-level portfolio data.
 * Provided by DashboardDataProvider (cloud-ui); consumed by widgets via usePortfolios() etc.
 * Defaults to empty/loading so widgets render safely before the provider mounts.
 */
export const PortfolioDataContext = createContext<PortfolioDataContextValue>({
  portfolios: [],
  pricingAssets: [],
  isLoading: true,
  error: false,
});

// Re-export codegen types so widget consumers don't need a direct @pulse-ui/codegen dep for these
export type { IPortfolioMetadata, IAccountMetadata };

/**
 * Workspace preset layout tree.
 * Mirrors FlexLayout's row/tabset/tab shape but is *our* canonical form,
 * authored in code. Root must be a row. Tabs are always inside a tabset.
 */
export interface WorkspacePresetTab {
  type: "tab";
  /** Stable UUID. Becomes the FlexLayout tab id and the widget instanceId. */
  id: string;
  /** Widget registry id (kebab-case, matches WidgetDefinition.id). */
  widgetId: string;
  /** Optional pre-assigned group. Ignored if the widget is not linkable. */
  groupId?: GroupId;
  /** Widget-specific initial config (merged over the widget's default config). */
  config?: Record<string, unknown>;
}

export interface WorkspacePresetTabset {
  type: "tabset";
  weight?: number;
  children: WorkspacePresetTab[];
}

export interface WorkspacePresetRow {
  type: "row";
  weight?: number;
  children: (WorkspacePresetRow | WorkspacePresetTabset)[];
}

export type WorkspacePresetCategory = "Trading" | "Operations" | "Admin";

export interface WorkspacePreset {
  /** Stable UUID, hand-authored. */
  presetId: string;
  /** Display name (e.g. 'Trader Desk'). */
  name: string;
  /** Short description shown in the menu. */
  description: string;
  category?: WorkspacePresetCategory;
  /** Sort order in UI. */
  order: number;
  /** If set, only users with at least one matching role see this preset. */
  requiredRoles?: string[];
  /** Root layout node. */
  layout: WorkspacePresetRow;
}

/**
 * Imperative actions exposed by the host workspace layout to widgets.
 * Provided via LayoutActionsContext by WorkspaceLayout; consumed via useLayoutActions().
 */
export interface LayoutActions {
  /**
   * Add a new widget instance to the workspace.
   * @param widgetId Widget registry id (matches WidgetDefinition.id)
   * @param name    Display name shown on the new tab
   * @param config  Per-instance config passed into the widget as `config` prop;
   *                serialized into the saved layout so it survives reload.
   */
  openWidget: (
    widgetId: string,
    name: string,
    config?: Record<string, unknown>,
  ) => void;
}

export const LayoutActionsContext = createContext<LayoutActions | null>(null);

/**
 * Widget toolbar control
 */
export interface WidgetControl {
  element: ReactNode;
  position?: "left" | "right";
}

/**
 * Widget settings panel
 */
export interface WidgetSettings {
  element: ReactNode;
}
